<template>
  <view-edit-panel
    class="transformer-general-data"
    v-model="transformerInfo"
    :disable-save="invalidForm"
    :loading="loading"
    :saving-confirm-code="confirmMessageCode"
    :saving-success-code="successMessageCode"
    @init-saving="checkSavingMessage"
    @changing="save"
    @cancel="cancel"
  >
    <b-container
      fluid
      slot-scope="data"
      class="transformer-general-data"
    >
      <b-row
        v-if="loading"
        class="transformer-general-data__loader"
        align-h="center"
        align-v="center"
      >
        <s-loader size="lg" />
      </b-row>
      <b-row v-else class="px-4">
        <transformer-form
          :temperature-variables="temperatureVariables"
          :transformer-info.sync="data.item"
          :last-reading-info="lastReadingInfo"
          :saving="data.saving"
          :edit-mode="data.editing"
          @form-status-update="formStatusUpdate"
          @variable-id-changed="getLastReadingWithAlert"
        />
      </b-row>
    </b-container>
  </view-edit-panel>
</template>

<script>
import { ModuleTypes, ModuleSubtype } from 'sigma-mappings';
import ViewEditPanel from '@/components/ViewEditPanel';
import GeneralDataService from '@/domain/services/installation/assetInfo/generalData';
import loadingMixin from '@/services/loadingMixin';
import Form from '@/domain/components/installation/assetInformation/transformers/generalData/Form';
import TreeviewHeaderSubtitleMixin from '@/components/treeview/treeviewHeaderSubtitleMixin';
import { formatter as dateTimeFormatter } from '@/services/dateTime/datetimeService';
import { formats } from '@/constants/localizer';

export default {
  name: 'TransformerGeneralData',
  components: {
    transformerForm: Form,
    ViewEditPanel,
  },
  mixins: [
    loadingMixin,
    TreeviewHeaderSubtitleMixin,
  ],
  data: () => ({
    haveChromatographyModule: false,
    havePhysicalChemicalModule: false,
    temperatureVariables: [],
    transformerInfo: {},
    invalidForm: true,
    confirmMessageCode: 'MC011',
    successMessageCode: 'MS006',
    lastReadingInfo: {},
  }),
  created() {
    this.getAssetModuleLookup();
    this.getTransformerData();
    this.getTemperatureVariables();
    this.updateHeaderTitle(this.$t('asset-information.asset-info.transformer-general-data'));
  },
  methods: {
    haveChangedVariableId(transformerInfo) {
      return transformerInfo.variableId !== this.transformerInfo.variableId;
    },
    haveMonitoredFieldsChanged(transformerInfo) {
      const physicalChemicalMonitoredFieldChanged = this
        .havePhysicalChemicalMonitoredFieldsChanged(transformerInfo);

      const chromatographyMonitoredFieldChanged = this
        .haveChromatographyMonitoredFieldsChanged(transformerInfo);

      return physicalChemicalMonitoredFieldChanged || chromatographyMonitoredFieldChanged;
    },
    haveChromatographyMonitoredFieldsChanged({
       superiorTension,
       inferiorTension,
       tertiaryTension,
    }) {
      if (!this.haveChromatographyModule) {
        return false;
      }

      const maxBefore = Math.max(
        Number(this.transformerInfo?.superiorTension || 0),
        Number(this.transformerInfo?.inferiorTension || 0),
        Number(this.transformerInfo?.tertiaryTension || 0),
      );

      const maxAfter = Math.max(
        Number(superiorTension || 0),
        Number(inferiorTension || 0),
        Number(tertiaryTension || 0),
      );

      return maxBefore !== maxAfter;
    },
    havePhysicalChemicalMonitoredFieldsChanged({
       transformerApplication,
    }) {
      if (!this.havePhysicalChemicalModule) {
        return false;
      }

      return transformerApplication !== this.transformerInfo.transformerApplication;
    },
    checkSuccessMessageCode(response) {
      const inReprocessing = response.reprocessingPhysicalChemicalSamplesStarted
            || response.reprocessingChromatographySamplesStarted;

      this.successMessageCode = inReprocessing ? 'MS035' : 'MS006';
    },
    checkSavingMessage(newValue) {
      const messagesCodes = [];
      const shouldAlertVariableChange = this.haveChangedVariableId(newValue);
      const shouldInformMonitoredFieldChange = this.haveMonitoredFieldsChanged(newValue);

      if (shouldInformMonitoredFieldChange) messagesCodes.push('MC056');
      if (shouldAlertVariableChange) messagesCodes.push('MC057');

      this.confirmMessageCode = messagesCodes.length > 0 ? messagesCodes : 'MC011';
    },
    cancel() {
      this.getLastReading(this.transformerInfo?.variableId);
    },
    save({ newValue, accept, reject }) {
      const { assetId } = this.$route.params;
      GeneralDataService
        .updateGeneralTransformerData(newValue, assetId)
        .then((response) => {
          this.checkSuccessMessageCode(response);
          accept();
        })
        .catch(reject);
    },
    getAssetModuleLookup() {
      const { assetId } = this.$route.params;
      GeneralDataService.getAssetModuleLookup(assetId, ModuleTypes.ENGINEERING)
        .then((response) => {
          this.haveChromatographyModule = response
            .some(x => x.subtype === ModuleSubtype.CHROMATOGRAPHY);

          this.havePhysicalChemicalModule = response
            .some(x => x.subtype === ModuleSubtype.PHYSICAL_CHEMICAL);
          });
    },
    getTransformerData() {
      const { assetId } = this.$route.params;
      this.startLoading();
      GeneralDataService.getGeneralTransformerData(assetId)
        .then((response) => {
          this.transformerInfo = response;
          this.getLastReading(response.variableId);
        })
        .finally(() => {
          this.endLoading();
        });
    },
    getTemperatureVariables() {
      const { installationId } = this.$route.params;
      this.startLoading();
      GeneralDataService.getTemperatureVariables(installationId)
        .then((response) => {
          this.temperatureVariables = response;
        })
        .finally(() => {
          this.endLoading();
        });
    },
    getLastReading(variableId) {
      if (!variableId) {
        return;
      }

      GeneralDataService.getLastReading(variableId)
        .then((response) => {
          this.lastReadingInfo = {
            ...response,
            readingDateTime: this.formatDate(response.readingDateTime),
          };
        });
    },
    getLastReadingWithAlert(variableId) {
      if (!variableId) {
        return;
      }

      GeneralDataService.getLastReading(variableId)
        .then((response) => {
          if (!response || !response.readingValue) {
            this.lastReadingInfo = {};
            this.$pushAlert({ code: 'MA107' });
            return;
          }

          this.lastReadingInfo = {
            ...response,
            readingDateTime: this.formatDate(response.readingDateTime),
          };
        })
        .catch(() => {
          this.lastReadingInfo = {};
          this.$pushAlert({ code: 'MA107' });
        });
    },
    formatDate(dateTime) {
      return dateTimeFormatter(dateTime, formats.DATETIME_SHORT_WITH_SECONDS);
    },
    formStatusUpdate(value) {
      this.invalidForm = value;
    },
  },
};
</script>

<style lang="sass" scoped>
.transformer-general-data
  &__loader
    height: 500px
</style>
